import { Divider, Grid, IconButton, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import i18n from 'simple-react-i18n'
import { bgGrey } from 'utils/constants/ColorTheme'
import StartIcon from '@mui/icons-material/Start'

const DIVIDER_WIDTH = 16
const DEFAULT_MIN_WIDTH = 200

export const getNotesPanelWidth = (minWidth = DEFAULT_MIN_WIDTH) => DIVIDER_WIDTH * 2 + minWidth

const ResizableNotes = ({
    panelTitle = '',
    panelContent,
    children,
    maxWidth = 1000,
    minWidth = DEFAULT_MIN_WIDTH,
}) => {
    const [isResizing, setIsResizing] = useState(false)
    const [drawerWidth, setDrawerWidth] = useState(minWidth)
    const [notes, setNotes] = useState()
    const dragRef = useRef(null)

    const handleChangeNotes = (e) => {
        const value = e.target.value
        setNotes(value)
    }

    const handleMouseDown = () => {
        setIsResizing(true)
    }

    useEffect(() => {
        const handleMouseUp = () => {
            setIsResizing(false)
        }

        const handleMouseMove = (e) => {
            if (!isResizing) return
            const newWidth = dragRef.current.getBoundingClientRect().right - e.clientX
            if (newWidth <= maxWidth && newWidth >= minWidth) {
                setDrawerWidth(newWidth)
            }
        }

        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isResizing, maxWidth, minWidth])

    useEffect(() => {
        setDrawerWidth(minWidth)
    }, [panelContent, panelTitle])

    return (
        <Grid container rowGap={2} sx={{ position: 'relative' }} height={'100%'}>
            <Grid size={'grow'} sx={{ overflowX: 'auto', height: '100%' }}>
                {children}
            </Grid>

            {/* Simulate notes panel width */}
            <Grid
                width={getNotesPanelWidth(minWidth)}
                minWidth={getNotesPanelWidth(minWidth)}
            />

            <Grid
                container
                sx={{ position: 'absolute', right: 0, backgroundColor: bgGrey }}
                height={'100%'}
                zIndex={2}
            >
                <Grid
                    container
                    onMouseDown={handleMouseDown}
                    width={DIVIDER_WIDTH}
                    sx={{ cursor: 'e-resize' }}
                >
                    <Divider
                        sx={{
                            height: '100%',
                        }}
                        orientation='vertical'
                        flexItem
                    />
                </Grid>
                <Grid
                    width={drawerWidth}
                    maxWidth={drawerWidth}
                    container
                    direction={'column'}
                    gap={2}
                    ref={dragRef}
                    justifyContent={'end'}
                >
                    <Grid
                        container
                        justifyContent={'space-between'}
                    >
                        <Typography
                            fontWeight={'bold'}
                            variant='h5'
                            onClick={() => setDrawerWidth(minWidth)}
                            sx={{ cursor: 'pointer' }}
                            width={'fit-content'}
                        >
                            {panelTitle || i18n.notes}
                        </Typography>
                        <IconButton
                            onClick={() => setDrawerWidth(minWidth)}
                            disabled={drawerWidth === minWidth}
                        >
                            <StartIcon/>
                        </IconButton>
                    </Grid>
                    {panelContent ?
                        panelContent :
                        <Grid
                            size={'grow'}
                        >
                            <textarea
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    margin: 0,
                                    resize: 'none',
                                    border: '1px lightGrey solid',
                                    borderRadius: 5,
                                    padding: '4px 8px 4px 8px',
                                    backgroundColor: 'white',
                                }}
                                value={notes}
                                onChange={handleChangeNotes}
                                placeholder={i18n.takeNotes}
                            />
                        </Grid>
                    }
                </Grid>
            </Grid>
        </Grid>
    )
}

ResizableNotes.propTypes = {
    panelTitle: PropTypes.string,
    panelContent: PropTypes.element,
    children: PropTypes.element,
    maxWidth: PropTypes.number,
    minWidth: PropTypes.number,
}

export default ResizableNotes